import React, { useState, useEffect, useRef, useContext } from 'react';
import { Close, Mic, Send } from '@material-ui/icons';
import { Bolt } from '@mui/icons-material';
import { Box, Button, Checkbox, CircularProgress, FormControlLabel, FormGroup, Grid, IconButton, InputAdornment, Menu, MenuItem, Modal, OutlinedInput, Snackbar, Tab, Tabs, Tooltip, Typography } from '@mui/material';

import EmojiConvertor from 'emoji-js'
import Emoji from '../Emoji';
import SendFileIcon from '../SendFileIcon';
import { ThemeContext } from '../ThemeContext/ThemeContext';
import { selectCurrentRoom, selectRoomAlert, setCurrentRoom } from '../../redux/room/room.actions';
import { useDispatch, useSelector } from 'react-redux';
import { set } from 'date-fns';
import { selectCurrentUser } from '../../redux/user/user.actions';
import http from '../../services/http';
import FilePreview from '../FilePreview';
import AudioRecorder from '../AudioRecorder';
import { adjustTextWithGpt } from '../../utils/aiTextTools';
import { toast } from 'react-toastify';
import discoveryIconWhite from '../../assets/icons/discovery_icon_white.png';
import discoveryIconBlack from '../../assets/icons/discovery_icon_black.png';

const ChatNewMessage = ({ handleSendingMessage, handleChangeClosingTime, sendFile, ...props }) => {
  const { theme } = useContext(ThemeContext);
  const [newMessage, setNewMessage] = useState("");
  const [emojiData, setEmojiData] = useState([]);
  const dispatch = useDispatch();
  const user = JSON.parse(localStorage.getItem("user"));
  const currentRoom = useSelector(selectCurrentRoom);
  const roomAlert = useSelector(selectRoomAlert);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [pendingClosure, setPendingClosure] = useState(props.room?.is_closing_time_cancelled === 1);
  const handleSendingMessageRef = useRef(handleSendingMessage);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const instanceInfo = useSelector(state => state.whatsapp.instanceInfo);
  const [isRecordingAudio, setIsRecordingAudio] = useState(false);
  const audioRecorderRef = useRef(null);
  const [showAudioRecorder, setShowAudioRecorder] = useState(false);
  const [startRecordingNow, setStartRecordingNow] = useState(false);
  const [gptAdjustingNewMessage, setGptAdjustingNewMessage] = useState(false);

  const [typingTimeout, setTypingTimeout] = useState(null);
  const [disableSend, setDisableSend] = useState(false);

  const [macros, setMacros] = useState([]);
  const [macroAnchorEl, setMacroAnchorEl] = useState(null);
  const [macroFilter, setMacroFilter] = useState('');

  useEffect(() => {
    http.get('/macros?for_select=1')
      .then((res) => setMacros(Array.isArray(res.data) ? res.data : []))
      .catch(() => setMacros([]));
  }, []);

  // Converte o corpo HTML da macro (vindo do editor rich-text) em texto plano
  // para preencher a caixa de mensagem do chat.
  const macroHtmlToText = (html) => {
    if (!html) return '';
    const container = document.createElement('div');
    container.innerHTML = html
      .replace(/<\/(p|div|br|li)>/gi, '\n')
      .replace(/<br\s*\/?>/gi, '\n');
    return (container.textContent || '').replace(/\n{3,}/g, '\n\n').trim();
  };

  const handleSelectMacro = (macro) => {
    setMacroAnchorEl(null);
    setMacroFilter('');
    const action = Array.isArray(macro?.actions)
      ? macro.actions.find((a) => a.type === 'set_message_body' && a.body != null)
      : null;
    if (!action) {
      toast.info('Esta macro não possui mensagem de texto.');
      return;
    }
    const text = macroHtmlToText(action.body);
    if (!text) {
      toast.info('Esta macro não possui mensagem de texto.');
      return;
    }
    // Preenche a caixa (acrescenta ao que já houver) para o operador revisar antes de enviar.
    setNewMessage((prev) => (prev && prev.trim() ? `${prev}\n${text}` : text));
  };

  const [tabValue, setTabValue] = useState(0);
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [emojiFilter, setEmojiFilter] = useState("");
  const [emojiPage, setEmojiPage] = useState(0);
  const emojisPerPage = 100;
  const paginatedEmojis = emojiData
    .filter((x) => x.short_name.startsWith(emojiFilter))
    .slice(emojiPage * emojisPerPage, (emojiPage + 1) * emojisPerPage);

  const openModal = () => setIsModalOpen(true);

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedFile(null);
  }

  useEffect(() => {
    selectedFile && selectedFile.type.startsWith('image/') && openModal();
  }, [selectedFile])

  // Insere no campo de mensagem a resposta do Orby (botão "Inserir") e confirma com um
  // ack para o Orby saber que havia um chat aberto (senão ele copia como fallback).
  useEffect(() => {
    const handler = (e) => {
      const text = e?.detail?.text || '';
      if (!text) return;
      setNewMessage((prev) => (prev && prev.trim() ? `${prev}\n${text}` : text));
      window.dispatchEvent(new CustomEvent('orbyInsertTextAck'));
    };
    window.addEventListener('orbyInsertText', handler);
    return () => window.removeEventListener('orbyInsertText', handler);
  }, [])

  useEffect(() => {
    if (newMessage) {
      if (typingTimeout) {
        clearTimeout(typingTimeout);
      }
      handleSendingMessageRef.current('typing');
      setTypingTimeout(setTimeout(() => {
        handleSendingMessageRef.current('stopped_typing');
      }, 1000));
    }

    return () => {
      if (typingTimeout) {
        clearTimeout(typingTimeout);
      }
    }
    // eslint-disable-next-line
  }, [newMessage]);

  useEffect(() => {
    setEmojiPage(0);
  }, [emojiFilter]);

  const handleNewMessageChange = (event) => {
    var emoji = new EmojiConvertor();
    emoji.replace_mode = 'unified';
    emoji.allow_native = true;

    let message = event.target.value;

    const regex = /:(\w+):/g;
    let match;
    while ((match = regex.exec(message)) !== null) {
      const emojiCode = match[1];
      const emojiDataItem = emojiData.find(x => x.short_name === emojiCode);
      if (emojiDataItem) {
        const emojiSymbol = emoji.replace_colons(`:${emojiCode}:`);
        message = message.replace(`:${emojiCode}:`, emojiSymbol);
      }
    }

    setNewMessage(message);
    // props.messageReadFocus();

    const lastColonIndex = message.lastIndexOf(":");
    if (lastColonIndex !== -1) {
      setShowEmojiPicker(true);
      setEmojiFilter(message.slice(lastColonIndex + 1));
    } else {
      setShowEmojiPicker(false);
    }
  };

  const OrbyInlineIcon = ({ loading: isLoading }) => {
    if (isLoading) {
      return (
        <CircularProgress
          size={18}
          thickness={6}
          sx={{
            color: 'primary.main',
            '@keyframes orbyPulse': {
              '0%': { opacity: 0.45 },
              '50%': { opacity: 1 },
              '100%': { opacity: 0.45 },
            },
            animation: 'orbyPulse 1s ease-in-out infinite',
          }}
        />
      );
    }
    return (
      <Box
        component="img"
        src={theme.palette.mode === 'dark' ? discoveryIconWhite : discoveryIconBlack}
        alt="Orby"
        sx={{ width: 18, height: 18, objectFit: 'contain' }}
      />
    );
  };

  const handleAdjustNewMessageWithGpt = async () => {
    const current = String(newMessage || '').trim();
    if (!current) {
      toast.warning('Digite uma mensagem antes de ajustar com Orby.');
      return;
    }
    if (gptAdjustingNewMessage) return;
    try {
      setGptAdjustingNewMessage(true);
      const res = await adjustTextWithGpt({ text: current, kind: 'mensagem do chat' });
      if (!res.ok) {
        toast.error(res.message || 'Não foi possível ajustar o texto.');
        return;
      }
      setNewMessage(res.text);
    } catch (e) {
      toast.error('Erro ao ajustar texto com Orby.');
    } finally {
      setGptAdjustingNewMessage(false);
    }
  };

  const handleSendMessage = () => {
    if (newMessage || selectedFile || (audioRecorderRef.current && audioRecorderRef.current.getCurrentAudio)) {
      let messageType = tabValue === 0 ? 1 : 5; // tipo baseado na aba selecionada

      // Legenda estilo WhatsApp: se anexou uma imagem E digitou texto, o texto vira a
      // LEGENDA da imagem (vai no campo message do arquivo), numa mensagem só — em vez
      // de ser enviado como uma mensagem de texto separada.
      const isImageFile = selectedFile && (selectedFile.type || '').startsWith('image/');
      const trimmedCaption = (newMessage || '').trim();

      if (selectedFile) {
        console.log(selectedFile)
        // Se houver um arquivo, envia o arquivo
        const formData = new FormData();
        formData.set('message', isImageFile ? trimmedCaption : selectedFile.name); // Imagem: legenda; senão: nome do arquivo
        formData.set('file', selectedFile); // O arquivo em si
        formData.set('room_id', props.roomId);
        formData.set('type', 1);
        formData.set('user_id', user.id);

        props.setSendingFile(true);

        // Faz a requisição para enviar o arquivo
        http.post(`/messages/file`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("token")}`
          }
        })
          .then(res => {
            setSelectedFile(null); // Limpa a seleção após o envio
            props.setSendingFile(false); // Para de indicar que está enviando
            sendFile && sendFile();
          })
          .catch(err => {
            console.error("Erro ao enviar arquivo", err);
            props.setSendingFile(false); // Em caso de erro, para o estado de envio
          });
      }

      if (audioRecorderRef.current && (audioRecorderRef.current.getCurrentAudio || audioRecorderRef.current.getAudioAfterStop)) {
        const current = audioRecorderRef.current.getCurrentAudio ? audioRecorderRef.current.getCurrentAudio() : null;
        
        if (current) {
          // Se já tem áudio pronto, envia imediatamente
          const base64Data = current.split(',')[1];
          const byteCharacters = atob(base64Data);
          const byteArrays = [];
          for (let i = 0; i < byteCharacters.length; i++) {
            byteArrays.push(byteCharacters.charCodeAt(i));
          }
          const byteArray = new Uint8Array(byteArrays);
          const blob = new Blob([byteArray], { type: 'audio/ogg; codecs=opus' });

          const formData = new FormData();
          formData.set('message', 'audio_' + Date.now() + '.ogg');
          formData.set('file', blob, `audio_${Date.now()}.ogg`);
          formData.set('room_id', props.roomId);
          formData.set('type', 1);
          formData.set('user_id', user.id);

          props.setSendingFile(true);
          props.sendMessage(formData, user, props.roomId, messageType, props.room.whatsapp_instance_id);
        } else if (audioRecorderRef.current.getAudioAfterStop) {
          // Se está gravando, para e aguarda o áudio
          audioRecorderRef.current.getAudioAfterStop().then(audioBase64 => {
            if (!audioBase64) return;

            const base64Data = audioBase64.split(',')[1];
            const byteCharacters = atob(base64Data);
            const byteArrays = [];
            for (let i = 0; i < byteCharacters.length; i++) {
              byteArrays.push(byteCharacters.charCodeAt(i));
            }
            const byteArray = new Uint8Array(byteArrays);
            const blob = new Blob([byteArray], { type: 'audio/ogg; codecs=opus' });

            const formData = new FormData();
            formData.set('message', 'audio_' + Date.now() + '.ogg');
            formData.set('file', blob, `audio_${Date.now()}.ogg`);
            formData.set('room_id', props.roomId);
            formData.set('type', 1);
            formData.set('user_id', user.id);

            props.setSendingFile(true);
            props.sendMessage(formData, user, props.roomId, messageType, props.room.whatsapp_instance_id);
          });
        }
      }

      if (isImageFile && trimmedCaption) {
        // O texto já foi enviado como legenda da imagem acima; só limpa a caixa.
        setNewMessage("");
      } else if (newMessage) {
        // Se houver uma mensagem de texto (sem imagem), envia a mensagem separada.
        props.sendMessage(newMessage, user, props.roomId, messageType, props.room.whatsapp_instance_id);
        setNewMessage("");
      }
    }
  };

  useEffect(() => {
    dispatch(setCurrentRoom({
      ...props.room, messages: [...props.messages,]
    }))
    // eslint-disable-next-line
  }, [props.messages]);

  const handleKeyPress = (e) => {
    if (e.shiftKey === false && e.key === 'Enter') {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleEmojiClick = (shortName) => {
    var emoji = new EmojiConvertor();
    emoji.replace_mode = 'unified';
    emoji.allow_native = true;

    const emojiSymbol = emoji.replace_colons(`:${shortName}:`);
    const message = newMessage.replace(/:\w*$/, emojiSymbol);

    setShowEmojiPicker(false);
    setNewMessage(message);
  };

  const handleEmojiScroll = (e) => {
    const element = e.target;

    if (element.scrollWidth - element.scrollLeft <= element.clientWidth) {
      setEmojiPage(prevPage => prevPage + 1);
    }
  };


  const handleCheckboxChange = (event) => {
    const isChecked = event;
    setPendingClosure(isChecked);
    props.setPendingClosure(isChecked);
    setShowSnackbar(true);
  };

  useEffect(() => {
    let timer;
    if (showSnackbar) {
      timer = setTimeout(() => {
        if (showSnackbar) {
          handleChangeClosingTime(props.roomId, pendingClosure);
        }
        setShowSnackbar(false);
      }, 5000);
    }

    return () => clearTimeout(timer);
  }, [showSnackbar, pendingClosure, props.roomId, handleChangeClosingTime]);

  useEffect(() => {
    if (props.messages.length !== 0) {
      setPendingClosure(props.room?.is_closing_time_cancelled === 1);
      const lastMessage = props.messages[props.messages.length - 1].message;
      const closingMessage = `Conversa encerrada pelo Cliente: ${currentRoom ? currentRoom.client ? currentRoom.client.name : '' : ''}`;
      if (lastMessage.toLowerCase().includes(closingMessage.toLowerCase())) {
        // console.log(lastMessage);
        setDisableSend(true);
      }
    }
  }, [props.room, props.roomId]);

  useEffect(() => {
    const handlePaste = (event) => {
      const clipboardItems = event.clipboardData.items;

      for (let i = 0; i < clipboardItems.length; i++) {
        const item = clipboardItems[i];

        if (item.kind === 'file') {
          const file = item.getAsFile();
          if (file) {
            setSelectedFile(file);
            break;
          }
        }
      }
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, []);

  return (
    <Grid
      container
      sx={{
        // Caixa de input como irmão normal do flex do chat (antes era
        // position:absolute; bottom:0, que sobrepunha o fim da lista de mensagens
        // e cortava as últimas). flex 0 0 auto = ocupa só a própria altura, abaixo
        // da lista, sem o xs={12} (flex-basis 100%) esticá-la.
        backgroundColor: theme.palette.background.default,
        flex: '0 0 auto', alignSelf: 'stretch',
        padding: 1, width: '100%'
      }}
    >
      {showEmojiPicker && (
        <div
          className="emoji-picker"
          onScroll={(e) => handleEmojiScroll(e)}
          style={{
            position: 'absolute',
            zIndex: 1,
            width: '100%',
            maxHeight: '3em',
            overflowX: 'scroll',
            whiteSpace: 'nowrap'
          }}
        >
          {paginatedEmojis.map((x, k) => {
            var emoji = new EmojiConvertor();
            emoji.replace_mode = 'unified';
            emoji.allow_native = true;
            return (
              <span
                key={k}
                onClick={() => handleEmojiClick(x.short_name)}
                title={x.short_name}  // Adicione esta linha
              >
                {emoji.replace_colons(`:${x.short_name}:`)}
              </span>
            );
          })}
        </div>
      )}
      {/* <Modal
        open={isModalOpen}
        onClose={closeModal}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <Grid sx={{ position: 'relative', width: 'auto', padding: 2, backgroundColor: 'white', borderRadius: 2 }}>
          {selectedFile && selectedFile.type.startsWith('image/') && (
            <img
              src={URL.createObjectURL(selectedFile)}
              alt="Pré-visualização"
              style={{ maxWidth: '100%', height: 'auto', borderRadius: '10px', width: '20rem' }}
            />
          )}
          <IconButton
            onClick={() => {
              setSelectedFile(null);
              closeModal(); // Fecha o modal
            }}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: theme.palette.error.main
            }}
          >
            <Close />
          </IconButton>
        </Grid>
      </Modal> */}

      {selectedFile && (
        <FilePreview
          selectedFile={selectedFile}
          setSelectedFile={setSelectedFile}
          handleSendMessage={handleSendMessage}
          newMessage={newMessage}
          setNewMessage={setNewMessage}
          isChat={true}
        />
      )}

      <Tabs value={tabValue} onChange={handleTabChange} sx={{
        backgroundColor: (theme) => theme.palette.background.default, marginBottom: 0.5, '& .MuiTabs-indicator': {
          backgroundColor: theme.palette.background.tabIndicator,
          height: '3px',
        },
        '& .MuiTab-root': {
          color: theme.palette.button.primary,
          padding: '1rem',
          borderBottom: 1, borderColor: 'divider',
          '&.Mui-selected': {
            backgroundColor: (theme) => theme.palette.secondary.main,
            // contrastText do secondary (quase branco nos dois temas) — antes usava
            // text.secondary (cinza), que ficava ilegível sobre o fundo #5b7fa3.
            color: (theme) => theme.palette.secondary.contrastText,
          },
          '&:hover': {
            backgroundColor: (theme) => theme.palette.background.hover,
            color: (theme) => theme.palette.text.primary.main,
          },
        }
      }}>
        <Tab label="Mensagens Normais" />
        <Tab label="Anotações Internas" />
      </Tabs>
      <Grid sx={{ width: '100%' }}>
        {selectedFile && !selectedFile.type.startsWith('image/') && (
          <Grid sx={{ display: 'flex', justifyContent: 'center', cursor: 'pointer' }} onClick={openModal}>
            <Grid>
              <img
                src={URL.createObjectURL(selectedFile)}
                alt="Pré-visualização"
                style={{ maxWidth: '100%', height: 'auto', borderRadius: '10px', width: '20rem' }}
              />
            </Grid>
            <Grid onClick={() => setSelectedFile(null)} sx={{
              display: 'flex',
              alignItems: 'flex-start',
              alignContent: 'center',
              cursor: 'pointer',
            }}>
              <Close />
            </Grid>
          </Grid>
        )}
      </Grid>
      {!showAudioRecorder && (
        <OutlinedInput
          disabled={disableSend || roomAlert}
          id="text-area"
          onKeyPress={handleKeyPress}
          onKeyDown={(e) => {
            const isShortcut = e.ctrlKey && e.shiftKey && String(e.key || '').toLowerCase() === 'g';
            if (isShortcut) {
              e.preventDefault();
              handleAdjustNewMessageWithGpt();
            }
          }}
          value={newMessage}
          fullWidth
          placeholder={roomAlert ? "Por favor entre no chat para conversar!" : "Mensagem"}
          multiline
          maxRows={4}
          onChange={(e) => handleNewMessageChange(e)}
          variant='outlined'
          sx={{
            color: theme.palette.text.secondary, margin: 0, bgcolor: tabValue ? 'rgba(255, 255, 0, 0.30)' : theme.palette.background.textField,
            '& .MuiInputBase-input': { color: theme.palette.text.primary },
            '&& .MuiInputBase-input.Mui-disabled': { color: 'red' }
          }}
          className={"new-message-input-field"}
          endAdornment={
            <InputAdornment position='end'>
              <Tooltip
                title={gptAdjustingNewMessage ? 'Orby está digitando...' : 'Ajustar texto (Orby) — Ctrl+Shift+G'}
                placement="top"
                arrow
              >
                <span>
                  <IconButton
                    size="small"
                    onClick={handleAdjustNewMessageWithGpt}
                    disabled={disableSend || roomAlert || gptAdjustingNewMessage}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      p: 0.75,
                      bgcolor: 'background.paper',
                      '&:hover': { bgcolor: 'action.hover' },
                      mr: 0.5,
                    }}
                    aria-label={gptAdjustingNewMessage ? 'Orby está digitando' : 'Ajustar texto com Orby'}
                  >
                    <OrbyInlineIcon loading={gptAdjustingNewMessage} />
                  </IconButton>
                </span>
              </Tooltip>
              <Emoji
                setNewMessage={setNewMessage}
                newMessage={newMessage}
                handleNewMessageChange={handleNewMessageChange}
                setEmojiData={setEmojiData}
                emojiData={emojiData}
              />
              {/* O `onClick` fica no IconButton, nao no icone dentro dele: com ele
                  no filho, so a area do desenho enviava -- a borda do botao nao
                  respondia -- e o MUI avisava isso no console a cada render. */}
              {/*
                * Enviar é a ação da barra, e precisa parecer isso.
                *
                * Medido: o único botão preenchido do compositor era o
                * "Emojis" (87x38, cinza cheio); enviar, microfone, anexo e
                * macro eram todos ícones transparentes de 30x30. O que mais
                * chamava atenção na barra de escrever era o seletor de
                * emoji, e a ação principal tinha o mesmo peso de um atalho.
                *
                * Preenchido e redondo, enviar passa a ser o único elemento
                * com fundo sólido ali -- que é o critério: um compositor tem
                * uma ação primária, não cinco iguais.
                */}
              <IconButton
                aria-label="send-message"
                onClick={handleSendMessage}
                sx={{
                  backgroundColor: 'primary.main',
                  color: (t) => t.palette.getContrastText(t.palette.primary.main),
                  '&:hover': { backgroundColor: 'primary.dark' },
                  '&.Mui-disabled': { backgroundColor: 'action.disabledBackground' },
                  /*
                   * O ícone TEM cor própria: `.send-message-button` em
                   * `room-list.css` o pinta de `--app-icon-muted`, que fazia
                   * sentido enquanto o botão era transparente. Com fundo
                   * sólido isso dá 1,17:1 -- um círculo cheio e nenhuma seta
                   * dentro, que foi o que apareceu na tela ao fazer esta
                   * mudança. Herdar devolve o contraste calculado acima.
                   */
                  '& .MuiSvgIcon-root': { color: 'inherit' },
                }}
              >
                <Send className="send-message-button" fontSize="medium" />
              </IconButton>
              <IconButton
                aria-label="record-audio"
                onClick={() => {
                  setShowAudioRecorder(true);
                  setStartRecordingNow(true);
                }}
              >
                <Mic />
              </IconButton>
              <IconButton aria-label="send-file" >
                <SendFileIcon setSelectedFile={setSelectedFile} />
              </IconButton>
              <Tooltip title="Inserir macro" placement="top" arrow>
                <span>
                  <IconButton
                    aria-label="insert-macro"
                    disabled={disableSend || roomAlert}
                    onClick={(e) => setMacroAnchorEl(e.currentTarget)}
                  >
                    <Bolt />
                  </IconButton>
                </span>
              </Tooltip>
              <Menu
                anchorEl={macroAnchorEl}
                open={Boolean(macroAnchorEl)}
                onClose={() => { setMacroAnchorEl(null); setMacroFilter(''); }}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                slotProps={{ paper: { sx: { width: 300, maxHeight: 380 } } }}
              >
                {/* Campo de busca: digitar filtra as macros pelo nome (sao muitas
                    para escolher em lista). stopPropagation evita o type-ahead do
                    Menu roubar as teclas. */}
                <Box sx={{ px: 1, pt: 0.5, pb: 1 }} onKeyDown={(e) => e.stopPropagation()}>
                  <OutlinedInput
                    autoFocus
                    size="small"
                    fullWidth
                    placeholder="Buscar macro..."
                    value={macroFilter}
                    onChange={(e) => setMacroFilter(e.target.value)}
                  />
                </Box>
                {(() => {
                  const filtered = macros.filter((m) =>
                    (m.name || '').toLowerCase().includes(macroFilter.trim().toLowerCase()));
                  if (macros.length === 0) {
                    return <MenuItem disabled>Nenhuma macro disponível</MenuItem>;
                  }
                  if (filtered.length === 0) {
                    return <MenuItem disabled>Nenhuma macro encontrada</MenuItem>;
                  }
                  return filtered.map((macro) => (
                    <MenuItem key={macro.id} onClick={() => handleSelectMacro(macro)}>
                      {macro.name}
                    </MenuItem>
                  ));
                })()}
              </Menu>
              {/* {props.room.user_id === user.id && (
              <FormGroup>
                <FormControlLabel labelPlacement='top' control={
                  <Checkbox checked={pendingClosure} onChange={(event) => handleCheckboxChange(event.target.checked)}
                  />} label="Encerramento" />
              </FormGroup>
            )} */}
            </InputAdornment>
          }
        />
      )}

      {showAudioRecorder && (
        <Grid container alignItems="center" justifyContent="flex-end" spacing={1}>
          <Grid item sx={{ width: '45%' }}>
            <AudioRecorder
              ref={audioRecorderRef}
              autoStart={startRecordingNow}
              onSave={() => { }}
              onRecordingStart={() => {
                setIsRecordingAudio(true);
                setStartRecordingNow(false);
              }}
              onRecordingEnd={() => setIsRecordingAudio(false)}
              onCancel={() => {
                setShowAudioRecorder(false);
                setStartRecordingNow(false);
              }}
            />
          </Grid>
          <Grid item>
            <IconButton
              aria-label="send-audio"
              onClick={() => { handleSendMessage(); setShowAudioRecorder(false); setStartRecordingNow(false); }}
            >
              <Send fontSize="large" />
            </IconButton>
          </Grid>
        </Grid>
      )}

      <Snackbar
        open={showSnackbar}
        autoHideDuration={6000}
        onClose={() => {
          setShowSnackbar(false);
          handleChangeClosingTime(props.roomId, pendingClosure);
        }}
        message="Encerramento do chat será ativado. Desfazer?"
        action={
          <Button color="secondary" size="small" onClick={() => { setPendingClosure(!pendingClosure); setShowSnackbar(false); }}>
            Desfazer
          </Button>
        }
      />
    </Grid>
  )
}


export default ChatNewMessage